import { AxiosError } from 'axios';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import api from 'api';
import type {
    TransferClientBlockedResponse,
    TransferClientSuccessResponse,
} from 'api/rest/clients';
import { RootState } from 'redux/store';
import {
    Actions,
    Modal,
    ModalHeader,
    PrimaryButton,
    SecondaryButton,
    Section,
    ModalNotification,
} from 'components/common/Modal';

type TransferClientOrganizationModalProps = {
    open: boolean;
    clientName: string;
    currentOrganizationName: string;
    targetOrganizationName: string;
    clientId: number;
    targetServiceProviderId: number;
    onClose: () => void;
    onSuccess: () => void;
    onInfo: (message: string) => void;
};

const TransferClientOrganizationModal = ({
    open,
    clientName,
    currentOrganizationName,
    targetOrganizationName,
    clientId,
    targetServiceProviderId,
    onClose,
    onSuccess,
    onInfo,
}: TransferClientOrganizationModalProps) => {
    const { language_setting: language } = useSelector((state: RootState) => state.user.entity);
    const { t } = useTranslation();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [blockers, setBlockers] = useState<TransferClientBlockedResponse['blockers'] | null>(
        null
    );
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const resetErrorState = () => {
        setBlockers(null);
        setErrorMessage(null);
    };

    const finishAndClose = () => {
        resetErrorState();
        onClose();
    };

    const handleClose = () => {
        if (isSubmitting) {
            return;
        }
        finishAndClose();
    };

    const buildAgentsClearedMessage = (response: TransferClientSuccessResponse) => {
        const applicationsWithRemovedAgents = (response.applications || []).filter(
            (application) => {
                const removedAgents = application.removed_agents;
                return (
                    removedAgents.consultant_id != null ||
                    removedAgents.backoffice_id != null ||
                    removedAgents.sales_id != null
                );
            }
        );

        if (applicationsWithRemovedAgents.length === 0) {
            return null;
        }

        return t('clientTransferAgentsCleared', {
            lng: language,
            defaultValue:
                'Agent assignments were cleared on {{count}} application(s) because those agents do not belong to the target organization.',
            count: applicationsWithRemovedAgents.length,
        });
    };

    const handleConfirm = async () => {
        resetErrorState();
        setIsSubmitting(true);

        try {
            const response = await api.clients.changeClientServiceProvider(
                clientId,
                targetServiceProviderId
            );
            const data = response.data;

            if ('msg' in data && !('from_service_provider_id' in data)) {
                onInfo(data.msg);
                finishAndClose();
                return;
            }

            const successData = data as TransferClientSuccessResponse;
            const agentsClearedMessage = buildAgentsClearedMessage(successData);
            if (agentsClearedMessage) {
                onInfo(agentsClearedMessage);
            }

            onSuccess();
            finishAndClose();
        } catch (error) {
            const axiosError = error as AxiosError<
                TransferClientBlockedResponse | { msg?: string }
            >;
            const status = axiosError.response?.status;
            const responseData = axiosError.response?.data;

            if (status === 409 && responseData && 'blockers' in responseData) {
                setBlockers(responseData.blockers);
                setErrorMessage(responseData.msg);
                return;
            }

            if (status === 403) {
                setErrorMessage(
                    t('forbidden', {
                        lng: language,
                        defaultValue: "You don't have permission to transfer this client.",
                    })
                );
                return;
            }

            if (responseData?.msg) {
                setErrorMessage(responseData.msg);
                return;
            }

            setErrorMessage(t('somethingWentWrong', { lng: language }));
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal open={open} onClose={handleClose} size="md">
            <ModalHeader
                eyebrow={t('transferClient', {
                    lng: language,
                    defaultValue: 'Transfer client',
                })}
                title={t('confirmClientTransfer', {
                    lng: language,
                    defaultValue: 'Confirm client transfer',
                })}
            />
            <Section>
                <p style={{ margin: 0, color: '#4b5563', lineHeight: 1.5, whiteSpace: 'pre-line' }}>
                    {t('confirmClientTransferMessage', {
                        lng: language,
                        defaultValue:
                            'Transfer {{clientName}} from {{currentOrganization}} to {{targetOrganization}}?\n\nApplicationssss, agent assignments, and chat membership may be updated.',
                        clientName,
                        currentOrganization: currentOrganizationName,
                        targetOrganization: targetOrganizationName,
                    })}
                </p>
            </Section>

            {(errorMessage || blockers) && (
                <ModalNotification
                    variant="error"
                    title={
                        errorMessage ||
                        t('transferBlocked', {
                            lng: language,
                            defaultValue: 'Transfer blocked',
                        })
                    }
                    message={
                        <>
                            {blockers?.map((blocker) => (
                                <div
                                    key={`${blocker.type}-${blocker.ids.join('-')}`}
                                    style={{ marginTop: blocker === blockers[0] ? 0 : 8 }}
                                >
                                    <strong>{blocker.type}</strong>: {blocker.message}
                                    {blocker.ids.length > 0
                                        ? ` (IDs: ${blocker.ids.join(', ')})`
                                        : ''}
                                </div>
                            ))}
                        </>
                    }
                />
            )}

            <Actions>
                <SecondaryButton type="button" onClick={handleClose} disabled={isSubmitting}>
                    {t('cancel', { lng: language, defaultValue: 'Cancel' })}
                </SecondaryButton>
                <PrimaryButton type="button" onClick={handleConfirm} disabled={isSubmitting}>
                    {isSubmitting
                        ? t('transferring', { lng: language, defaultValue: 'Transferring...' })
                        : t('confirm', { lng: language, defaultValue: 'Confirm' })}
                </PrimaryButton>
            </Actions>
        </Modal>
    );
};

export default TransferClientOrganizationModal;
